import { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { clearTokens } from '@/lib/api';
import DevShell from './DevShell';
import BugReportModal from './BugReportModal';
import TopNav from './TopNav';

// ============================================================================
// Impersonation Bar
//
// Retained verbatim from the previous Layout (ENGINE-BRIEF section 2). The raw
// red is a deliberate impersonation warning, out of scope for the role-token
// migration, and is not reproduced anywhere in the new top nav.
// ============================================================================

function ImpersonationBar() {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [targetEmail, setTargetEmail] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const impersonating = localStorage.getItem('speytech_impersonating');
    const email = localStorage.getItem('speytech_impersonating_email');
    setIsImpersonating(impersonating === 'true');
    setTargetEmail(email);
  }, []);

  const handleExitImpersonation = () => {
    localStorage.removeItem('speytech_impersonating');
    localStorage.removeItem('speytech_impersonating_email');
    clearTokens();
    navigate('/login');
  };

  if (!isImpersonating) return null;

  return (
    <div className="bg-red-600 text-white px-4 py-2 text-sm flex items-center justify-between shrink-0">
      <div className="flex items-center gap-2">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span className="font-medium">Impersonating User</span>
        {targetEmail && <span className="opacity-80">({targetEmail})</span>}
      </div>
      <button
        onClick={handleExitImpersonation}
        className="bg-red-700 hover:bg-red-800 px-3 py-1 rounded text-sm font-medium transition-colors"
      >
        Exit Impersonation
      </button>
    </div>
  );
}

// ============================================================================
// Layout
//
// The customer-portal shell. A single role-token top nav replaces the retired
// left sidebar (ENGINE-BRIEF-TOPNAV-SIDEBAR-RETIRE-v1.1). Vertical flex column:
//
//   skip link        (first focusable, targets #main)
//   ImpersonationBar (shrink-0, conditional)
//   TopNav           (shrink-0, the banner)
//   main             (flex-1, scrolls, focusable target for the skip link)
//   DevShell         (docks at the bottom, shrinks main when expanded)
//
// DevShell is retained untouched, including its deliberate raw gray dark
// exception; it is self-triggering and needs no bar control. AdminLayout,
// MigrationLayout and the nested SettingsLayout are unchanged.
// ============================================================================

export default function Layout() {
  const [bugReportOpen, setBugReportOpen] = useState(false);

  return (
    <div className="relative h-[100dvh] flex flex-col bg-surface-secondary overflow-hidden">
      {/* C-IOS-1: the shell sizes on 100dvh, not 100vh, so iOS Safari's collapsing
          bottom toolbar does not leave the bottom tab bar clipped or floating. */}
      <a
        href="#main"
        className="absolute left-2 -top-12 focus:top-2 z-[200] bg-accent-cta text-on-accent px-3.5 py-2 rounded-lg text-[13px] font-semibold transition-all motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        Skip to content
      </a>

      <ImpersonationBar />

      <TopNav onOpenBugReport={() => setBugReportOpen(true)} />

      <main
        id="main"
        tabIndex={-1}
        className="flex-1 overflow-auto min-h-0 px-4 pt-4 pb-[calc(96px+env(safe-area-inset-bottom))] lg:px-8 lg:py-8 bg-surface-secondary focus:outline-none"
      >
        <Outlet />
      </main>

      <DevShell />

      <BugReportModal isOpen={bugReportOpen} onClose={() => setBugReportOpen(false)} />
    </div>
  );
}
